import React, { useState } from "react"
import { useNavigate } from "react-router-dom"
import i18n from "i18n"
import PropTypes from "prop-types"
import { Box, IconButton, Menu, MenuItem } from "@mui/material"
import MoreVertIcon from "@mui/icons-material/MoreVert"
import { useDeletePurchaseOrder } from "../hooks/usePurchaseOrders"
import { useAuthContext } from "context/auth/authContext"
import { useSnackbar } from "notistack"
import { getPurchaseOrderRowId } from "../utils/purchaseOrderUtils"
import DeletePurchaseOrderDialog from "./DeletePurchaseOrderDialog"

const ActionsMenu = ({ row, projectId }) => {
  const [anchorEl, setAnchorEl] = useState(null)
  const open = Boolean(anchorEl)

  const handleMenuOpen = event => {
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const navigate = useNavigate()
  const [confirmOpen, setConfirmOpen] = useState(false)

  const { user } = useAuthContext()
  const organizationId = user?.["custom:organizationId"]
  const deletePurchaseOrder = useDeletePurchaseOrder()
  const { enqueueSnackbar } = useSnackbar()

  const purchaseOrderId = getPurchaseOrderRowId(row)
  const linkedExpenseId =
    row?.expenseId ?? row?.expense_id ?? row?.expense?.id ?? null
  const hasLinkedExpense =
    linkedExpenseId != null && String(linkedExpenseId).trim() !== ""

  const navigateToEdit = () => {
    if (purchaseOrderId != null && String(purchaseOrderId) !== "") {
      navigate(
        projectId
          ? `/purchase-order/${purchaseOrderId}/${projectId}`
          : `/purchase-order/${purchaseOrderId}`,
      )
    }
  }

  const handleEditClick = () => {
    navigateToEdit()
    handleMenuClose()
  }

  const handleDeleteClick = () => {
    setConfirmOpen(true)
    handleMenuClose()
  }

  const handleConfirmDelete = () => {
    setConfirmOpen(false)
    if (
      organizationId &&
      purchaseOrderId != null &&
      String(purchaseOrderId) !== ""
    ) {
      deletePurchaseOrder.mutate(
        { organizationId, purchaseOrderId },
        {
          onError: () => {
            enqueueSnackbar(i18n.t("purchaseOrders.deleteError"), {
              variant: "error",
            })
          },
        },
      )
    }
  }

  const handleCancelDelete = () => {
    setConfirmOpen(false)
  }

  return (
    <Box onClick={e => e.stopPropagation()}>
      <IconButton size="small" aria-label="more" onClick={handleMenuOpen}>
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem onClick={handleEditClick}>
          {i18n.t("purchaseOrders.editPurchaseOrder")}
        </MenuItem>
        <MenuItem onClick={handleDeleteClick}>
          {i18n.t("purchaseOrders.deletePurchaseOrder")}
        </MenuItem>
      </Menu>
      <DeletePurchaseOrderDialog
        open={confirmOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        hasLinkedExpense={hasLinkedExpense}
      />
    </Box>
  )
}

ActionsMenu.propTypes = {
  row: PropTypes.object.isRequired,
  projectId: PropTypes.string,
}

export default ActionsMenu
